import { ContentSwitcher, IconButton } from "@carbon6/c6-frontend-design";
import { useEffect, useRef, useState } from "react";
import { appConfig } from "@/shared/config";
import { toErrorLogContext } from "@/shared/http";
import { createLogger } from "@/shared/logging";
import { CopyButton } from "@/shared/ui/CopyButton";
import guide from "@/shared/ui/guide.module.css";
import { issueConnectCode, type ConnectCode } from "../api/connectCodeApi";
import { connectCodeErrorMessage } from "../feature/connectCodeError";
import {
  CLAUDE_REMOVE_COMMAND,
  CODEX_LOGIN_COMMAND,
  formatConnectCode,
  installCommand,
  loginCommand,
  remainingLabel,
  resolveMcpUrl,
  type McpClient,
} from "../feature/connectCommands";
import { startExpiryTicks } from "../feature/expiryTicks";
import styles from "./McpConnectPanel.module.css";

const logger = createLogger("modules/mcp/connectPanel");

const CLIENT_LABEL: Record<McpClient, string> = { codex: "Codex", claude: "Claude Code" };

type IssueState = {
  /** 마지막으로 받은 코드. 다시 발급이 실패해도 남는다. */
  code: ConnectCode | null;
  issuing: boolean;
  /** 마지막 발급 요청의 실패 문구. 새 요청을 시작하면 지운다. */
  failure: string | null;
};

/**
 * MCP 클라이언트(Codex · Claude Code)를 spnp 에 연결하는 방법을 안내한다.
 *
 * 연결 코드는 누를 때만 발급한다. 발급 한도가 작고(Overlord, 분당 몇 회) 코드는 한 번만 쓰인다.
 * 이 화면은 토큰을 받지 않는다 — 코드를 토큰으로 바꾸는 일은 설치 스크립트가 한다.
 *
 * `active` 가 꺼지면(창 최소화) 남은 시간을 세지 않는다. 다시 켜지면 지금 시각부터 센다.
 */
export function McpConnectPanel({ active = true }: { active?: boolean }) {
  const [client, setClient] = useState<McpClient>("codex");
  const [issue, setIssue] = useState<IssueState>({ code: null, issuing: false, failure: null });
  const [nowMs, setNowMs] = useState(() => Date.now());
  // 화면을 닫은 뒤 늦게 온 발급 응답이 상태를 바꾸지 않게 한다.
  const generation = useRef(0);
  const mcpUrl = resolveMcpUrl(appConfig.mcpUrl, window.location.origin);
  const code = issue.code;
  const expiresAtMs = code === null ? null : code.expiresAtMs;

  useEffect(() => {
    const current = generation;
    return () => {
      current.current += 1;
    };
  }, []);

  useEffect(() => {
    if (expiresAtMs === null || !active) {
      return;
    }
    return startExpiryTicks(expiresAtMs, setNowMs);
  }, [expiresAtMs, active]);

  useEffect(() => {
    if (mcpUrl === null) {
      // 값은 남기지 않는다. 인증 정보가 든 주소일 수 있다.
      logger.error(
        "MCP URL setting was rejected. Expected an absolute http(s) URL that is safe in a shell command.",
      );
    }
  }, [mcpUrl]);

  async function handleIssue(): Promise<void> {
    const requested = (generation.current += 1);
    // 앞의 코드는 지우지 않는다. 다시 발급이 실패해도 앞의 코드는 만료 전까지 쓸 수 있다.
    setIssue((previous) => ({ ...previous, issuing: true, failure: null }));
    try {
      const issued = await issueConnectCode();
      if (generation.current === requested) {
        // 타이머는 만료 뒤와 비활성 중에 멈춘다. 새 코드의 남은 시간을 옛 시각으로 그리지 않게 한다.
        setNowMs(Date.now());
        setIssue({ code: issued, issuing: false, failure: null });
      }
    } catch (error: unknown) {
      logger.warn("Connect code issue failed.", toErrorLogContext(error));
      if (generation.current === requested) {
        const failure = connectCodeErrorMessage(error);
        setIssue((previous) => ({ ...previous, issuing: false, failure }));
      }
    }
  }

  // 주소가 틀리면 명령을 만들 수 없다. 쓸 수 없는 코드를 발급하지 않게 발급 버튼도 두지 않는다.
  if (mcpUrl === null) {
    return (
      <div className={guide.text}>
        <p role="alert">
          MCP 주소 설정이 올바르지 않아 연결 명령을 만들 수 없습니다. 관리자에게 문의해 주세요.
        </p>
      </div>
    );
  }

  const remaining = code === null ? null : remainingLabel(code.expiresAtMs, nowMs);
  const login = loginCommand(client, mcpUrl);

  return (
    <div className={guide.text}>
      <p>
        MCP 클라이언트에서 spnp 툴을 쓰도록 연결합니다. 연결한 뒤 클라이언트에서{" "}
        <code>mcp_usage_guide</code> 를 호출하면 연결을 확인할 수 있습니다.
      </p>
      <ContentSwitcher
        label="MCP 클라이언트"
        value={client}
        options={[
          { value: "codex", label: CLIENT_LABEL.codex },
          { value: "claude", label: CLIENT_LABEL.claude },
        ]}
        onValueChange={(value) => {
          if (value === "codex" || value === "claude") setClient(value);
        }}
      />

      <section className={styles.section} aria-label="원클릭 연결">
        <h4>원클릭 연결</h4>
        <p>
          연결 코드를 발급한 뒤 {CLIENT_LABEL[client]} 를 쓰는 컴퓨터의 터미널에서 명령을 실행해
          주세요. 브라우저가 없는 원격 컴퓨터에서도 연결할 수 있습니다.
        </p>
        <div>
          <IconButton
            text={issue.issuing ? "발급하는 중…" : code !== null ? "다시 발급" : "연결 코드 발급"}
            variant="solid"
            disabled={issue.issuing}
            onClick={() => void handleIssue()}
          />
        </div>
        {issue.failure !== null ? <p role="alert">{issue.failure}</p> : null}
        {code !== null && remaining === null ? (
          <p role="status">연결 코드가 만료되었습니다. 다시 발급해 주세요.</p>
        ) : null}
        {code !== null && remaining !== null ? (
          <>
            {/* 남은 시간은 매초 바뀐다. `status` 안에 두면 스크린리더가 매초 다시 읽는다. */}
            <p>
              <span role="status">
                연결 코드 <strong className={styles.code}>{formatConnectCode(code.code)}</strong>
              </span>{" "}
              · 남은 시간 <span role="timer">{remaining}</span>
            </p>
            <div className={guide.row}>
              <span>실행할 명령</span>
              <CopyButton label="원클릭 명령" value={installCommand(client, mcpUrl, code.code)} />
            </div>
            <pre>{installCommand(client, mcpUrl, code.code)}</pre>
            <p>
              코드는 한 번만 쓸 수 있습니다. 스크립트를 먼저 읽으려면 명령에서 <code>| sh</code> 와
              그 뒤를 지우고 실행해 주세요.
            </p>
          </>
        ) : null}
      </section>

      <section className={styles.section} aria-label="로그인으로 연결">
        <h4>로그인으로 연결</h4>
        <p>
          {CLIENT_LABEL[client]} 를 쓰는 컴퓨터에서 브라우저를 열 수 있으면 아래 명령으로 연결할 수
          있습니다. 클라이언트가 브라우저에서 로그인 화면을 엽니다.
        </p>
        <div className={guide.row}>
          <span>실행할 명령</span>
          <CopyButton label="로그인 명령" value={login} />
        </div>
        <pre>{login}</pre>
        {client === "codex" ? (
          <p>
            명령이 로그인 화면을 열지 않으면 <code>{CODEX_LOGIN_COMMAND}</code> 을 실행해 주세요.
          </p>
        ) : (
          <>
            <p>
              등록한 뒤 Claude Code 안에서 <code>/mcp</code> 를 실행하고 spnp 를 골라 로그인해
              주세요.
            </p>
            <p>
              spnp 가 이미 등록되어 있으면 먼저 <code>{CLAUDE_REMOVE_COMMAND}</code> 를 실행해
              주세요. Claude Code 는 같은 이름의 등록을 덮어쓰지 않습니다.
            </p>
          </>
        )}
        <p>
          로그인의 마지막 단계는 클라이언트를 실행한 컴퓨터의 localhost 주소로 돌아옵니다.
          브라우저가 다른 컴퓨터에 있으면 원클릭 연결을 쓰거나, <code>ssh -L</code> 로 그 포트를
          브라우저가 있는 컴퓨터로 넘겨 주세요.
        </p>
      </section>

      <p>연결은 약 3개월 뒤 만료됩니다. 만료되면 위 방법 중 하나로 다시 연결해 주세요.</p>
    </div>
  );
}
